import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Alert, Spinner, Nav, Tab } from 'react-bootstrap';

// URL base del backend
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

const SystemSettings = ({ onSuccess }) => {
  // =====================================================
  // ESTADO
  // =====================================================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(null);
  const [seccionActiva, setSeccionActiva] = useState('otp');
  
  // Configuraciones
  const [configOTP, setConfigOTP] = useState(null);
  const [configDocumentos, setConfigDocumentos] = useState(null);
  const [configSeguridad, setConfigSeguridad] = useState(null);

  // =====================================================
  // CARGAR CONFIGURACIÓN
  // =====================================================
  useEffect(() => {
    cargarConfiguracion();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const correo = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/sistema`, {
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setConfigOTP(data.data.otp || {});
        setConfigDocumentos(data.data.documentos || {});
        setConfigSeguridad(data.data.seguridad || {});
      } else {
        setError(data.error || 'Error al cargar configuración');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // GUARDAR SECCIÓN
  // =====================================================
  const guardarSeccion = async (seccion, datos) => {
    try {
      setGuardando(seccion);
      setError(null);

      const correo = localStorage.getItem('correo');

      const response = await fetch(`${API_BASE}/api/config/sistema/${seccion}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        },
        body: JSON.stringify(datos)
      });

      const data = await response.json();

      if (data.success) {
        onSuccess?.(`Configuración de ${seccion} actualizada`);
        
        switch(seccion) {
          case 'otp':
            setConfigOTP(data.data);
            break;
          case 'documentos':
            setConfigDocumentos(data.data);
            break;
          case 'seguridad':
            setConfigSeguridad(data.data);
            break;
        }
      } else {
        setError(data.error || 'Error al guardar');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setGuardando(null);
    }
  };

  // =====================================================
  // RESTABLECER SECCIÓN
  // =====================================================
  const restablecerSeccion = async (seccion) => {
    if (!window.confirm(`¿Restablecer configuración de ${seccion} a valores por defecto?`)) {
      return;
    }

    try {
      setGuardando(seccion);
      setError(null);

      const correo = localStorage.getItem('correo');

      const response = await fetch(`${API_BASE}/api/config/sistema/${seccion}/restablecer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        }
      });

      const data = await response.json();

      if (data.success) {
        onSuccess?.(`Configuración de ${seccion} restablecida`);
        cargarConfiguracion();
      } else {
        setError(data.error || 'Error al restablecer');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setGuardando(null);
    }
  };

  // =====================================================
  // HANDLERS DE INPUTS
  // =====================================================
  const handleOTPChange = (campo, valor) => {
    setConfigOTP(prev => ({ ...prev, [campo]: valor }));
  };

  const handleDocumentosChange = (campo, valor) => {
    setConfigDocumentos(prev => ({ ...prev, [campo]: valor }));
  };

  const handleSeguridadChange = (campo, valor) => {
    setConfigSeguridad(prev => ({ ...prev, [campo]: valor }));
  };

  // =====================================================
  // RENDER - LOADING
  // =====================================================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Cargando configuración...</span>
      </div>
    );
  }

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1">
          <i className="bi bi-sliders text-primary me-2"></i>
          Configuración del Sistema
        </h5>
        <small className="text-muted">Ajusta los parámetros de funcionamiento</small>
      </div>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Tabs de secciones */}
      <Tab.Container activeKey={seccionActiva} onSelect={setSeccionActiva}>
        <Nav variant="pills" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="otp">
              <i className="bi bi-key me-2"></i>OTP
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="documentos">
              <i className="bi bi-file-earmark me-2"></i>Documentos
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="seguridad">
              <i className="bi bi-shield-lock me-2"></i>Seguridad
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* ==================== SECCIÓN OTP ==================== */}
          <Tab.Pane eventKey="otp">
            {configOTP && (
              <div>
                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Tiempo de expiración (minutos)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="30"
                        value={configOTP.expiracionMinutos || 5}
                        onChange={(e) => handleOTPChange('expiracionMinutos', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Entre 1 y 30 minutos</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Longitud del código</Form.Label>
                      <Form.Control
                        type="number"
                        min="4"
                        max="8"
                        value={configOTP.longitud || 6}
                        onChange={(e) => handleOTPChange('longitud', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Entre 4 y 8 dígitos</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Intentos máximos</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="10"
                        value={configOTP.intentosMaximos || 3}
                        onChange={(e) => handleOTPChange('intentosMaximos', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Intentos antes de bloquear</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Canales de envío</Form.Label>
                  <div>
                    <Form.Check
                      type="checkbox"
                      id="sms"
                      label="Enviar por SMS"
                      checked={configOTP.enviarPorSMS || false}
                      onChange={(e) => handleOTPChange('enviarPorSMS', e.target.checked)}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      id="email"
                      label="Enviar por Email"
                      checked={configOTP.enviarPorEmail || false}
                      onChange={(e) => handleOTPChange('enviarPorEmail', e.target.checked)}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      id="whatsapp"
                      label="Enviar por WhatsApp"
                      checked={configOTP.enviarPorWhatsApp || false}
                      onChange={(e) => handleOTPChange('enviarPorWhatsApp', e.target.checked)}
                    />
                  </div>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-secondary"
                    onClick={() => restablecerSeccion('otp')}
                    disabled={guardando === 'otp'}
                  >
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Restablecer
                  </Button>
                  <Button 
                    variant="primary"
                    onClick={() => guardarSeccion('otp', configOTP)}
                    disabled={guardando === 'otp'}
                  >
                    {guardando === 'otp' ? (
                      <><Spinner size="sm" className="me-2" />Guardando...</>
                    ) : (
                      <><i className="bi bi-check-lg me-2"></i>Guardar</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Tab.Pane>

          {/* ==================== SECCIÓN DOCUMENTOS ==================== */}
          <Tab.Pane eventKey="documentos">
            {configDocumentos && (
              <div>
                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Días de expiración</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="90"
                        value={configDocumentos.expiracionDias || 7}
                        onChange={(e) => handleDocumentosChange('expiracionDias', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Días antes de que expire un documento</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Recordatorio (días antes)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="30"
                        value={configDocumentos.recordatorioAntesDeVencer || 2}
                        onChange={(e) => handleDocumentosChange('recordatorioAntesDeVencer', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Días antes para enviar recordatorio</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Máximo de reenvíos</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="10"
                        value={configDocumentos.maximoReenvios || 3}
                        onChange={(e) => handleDocumentosChange('maximoReenvios', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Número máximo de reenvíos</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Opciones</Form.Label>
                  <div>
                    <Form.Check
                      type="checkbox"
                      id="recordatorios"
                      label="Enviar recordatorios automáticos"
                      checked={configDocumentos.enviarRecordatorios || false}
                      onChange={(e) => handleDocumentosChange('enviarRecordatorios', e.target.checked)}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      id="anular"
                      label="Anular automáticamente documentos vencidos"
                      checked={configDocumentos.anularDocumentosVencidos || false}
                      onChange={(e) => handleDocumentosChange('anularDocumentosVencidos', e.target.checked)}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      id="reenvio"
                      label="Permitir reenvío de documentos"
                      checked={configDocumentos.permitirReenvio || false}
                      onChange={(e) => handleDocumentosChange('permitirReenvio', e.target.checked)}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      id="selfie"
                      label="Requerir selfie en el proceso de firma"
                      checked={configDocumentos.requiereSelfie || false}
                      onChange={(e) => handleDocumentosChange('requiereSelfie', e.target.checked)}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      id="gps"
                      label="Requerir ubicación GPS"
                      checked={configDocumentos.requiereGPS || false}
                      onChange={(e) => handleDocumentosChange('requiereGPS', e.target.checked)}
                    />
                  </div>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-secondary"
                    onClick={() => restablecerSeccion('documentos')}
                    disabled={guardando === 'documentos'}
                  >
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Restablecer
                  </Button>
                  <Button 
                    variant="primary"
                    onClick={() => guardarSeccion('documentos', configDocumentos)}
                    disabled={guardando === 'documentos'}
                  >
                    {guardando === 'documentos' ? (
                      <><Spinner size="sm" className="me-2" />Guardando...</>
                    ) : (
                      <><i className="bi bi-check-lg me-2"></i>Guardar</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Tab.Pane>

          {/* ==================== SECCIÓN SEGURIDAD ==================== */}
          <Tab.Pane eventKey="seguridad">
            {configSeguridad && (
              <div>
                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Expiración de sesión (horas)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="168"
                        value={configSeguridad.sesionExpiracionHoras || 24}
                        onChange={(e) => handleSeguridadChange('sesionExpiracionHoras', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Horas antes de que expire</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Intentos antes de bloqueo</Form.Label>
                      <Form.Control
                        type="number"
                        min="3"
                        max="10"
                        value={configSeguridad.bloqueoUsuarioIntentos || 5}
                        onChange={(e) => handleSeguridadChange('bloqueoUsuarioIntentos', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Intentos fallidos permitidos</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Duración del bloqueo (min)</Form.Label>
                      <Form.Control
                        type="number"
                        min="5"
                        max="1440"
                        value={configSeguridad.bloqueoUsuarioMinutos || 30}
                        onChange={(e) => handleSeguridadChange('bloqueoUsuarioMinutos', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">Minutos que dura el bloqueo</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Opciones</Form.Label>
                  <div>
                    <Form.Check
                      type="checkbox"
                      id="azure"
                      label="Requerir autenticación con Microsoft Azure AD"
                      checked={configSeguridad.requiereAzureAD || false}
                      onChange={(e) => handleSeguridadChange('requiereAzureAD', e.target.checked)}
                    />
                  </div>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-secondary"
                    onClick={() => restablecerSeccion('seguridad')}
                    disabled={guardando === 'seguridad'}
                  >
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Restablecer
                  </Button>
                  <Button 
                    variant="primary"
                    onClick={() => guardarSeccion('seguridad', configSeguridad)}
                    disabled={guardando === 'seguridad'}
                  >
                    {guardando === 'seguridad' ? (
                      <><Spinner size="sm" className="me-2" />Guardando...</>
                    ) : (
                      <><i className="bi bi-check-lg me-2"></i>Guardar</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default SystemSettings;
